import _ from 'lodash';
import { sqTrendMetricStore, sqTrendStore, sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { API_TYPES } from '@/main/app.constants';
import { CANCELLATION_GROUP_GUID_SEPARATOR, cancelGroup } from '@/requests/pendingRequests.utilities';
import { fetchItemUsages } from '@/search/search.actions';
import { addMetricColumn } from '@/tableBuilder/tableBuilder.actions';
import { findItemIn, getItemType, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { recordDataStatus } from '@/trendData/itemRuntime.utilities';
import {
  addTrendItem,
  catchItemDataFailure,
  fetchItemProps,
  setCustomizationProps,
  setItemSelected,
  setTrendItemProps,
} from '@/trendData/trend.actions';
import { addChildItem, addShadedArea } from '@/trendData/trendChildItem.actions';
import {
  DASH_STYLES,
  FORMULA_FRAGMENT_TYPE,
  ITEM_CHILDREN_TYPES,
  ITEM_DATA_STATUS,
  ITEM_TYPES,
  SHADED_AREA_TYPES,
} from '@/trendData/trendData.constants';
import { updateLaneDisplay } from '@/trendData/yAxis.actions';
import { warnToast } from '@/utilities/toast.utilities';
import { tabsetChangeTab } from '@/worksheet/worksheet.actions';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqMetricsApi } from '@seeqdev/api/endpoint/MetricsApi';
import { ProcessTypeEnum } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { AxiosPromise } from 'axios';
import type { PushOption } from '@/core/flux.service';
import type { DataStatusPayload } from '@/trendData/itemStoreBase.types';
import type { MetricItem } from '@/trendData/trendData.types';
import type { ThresholdOutputV1 } from '@seeqdev/api/model/ThresholdOutputV1';
import type { ThresholdMetricOutputV1 } from '@seeqdev/api/model/models';

export function setMetricDataStatusLoading(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.LOADING, { id });
}

export function setMetricDataStatusPresent(payload: DataStatusPayload): void {
  recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
}

export function addMetric(item: MetricItem, props?: Record<string, unknown>, option?: PushOption) {
  if (!sqTrendMetricStore.findItem(item.id)) {
    const payload = {
      id: item.id,
      lane: item.lane ? item.lane : sqTrendStore.getNextLane(false),
      alignment: item.axisAlign ? item.axisAlign : sqTrendStore.nextAlignment,
      name: item.name,
      isArchived: !!item.isArchived,
      color: item.color,
      effectivePermissions: item.effectivePermissions,
    };

    flux.dispatch('TREND_ADD_METRIC', payload, option);
  }

  setTrendItemProps(item.id, props, option);

  return Promise.all([fetchItemProps(item.id)]).then(() => {
    // If metric is Condition then automatically add the bounding condition to the trend when the metric is added
    const addedItem = findItemIn(getTrendStores(), item.id);
    const metricProcessType = _.get(addedItem, 'definition.processType');
    const metricBoundingCondition = _.get(addedItem, 'definition.boundingCondition');
    if (metricProcessType === ProcessTypeEnum.Condition && metricBoundingCondition) {
      addTrendItem(metricBoundingCondition, props, PUSH_IGNORE);
    }
    if (metricProcessType === ProcessTypeEnum.Condition || metricProcessType === ProcessTypeEnum.Continuous) {
      addMetricColumn(item);
    }
  });
}

export function fetchMetric(id: string): AxiosPromise<ThresholdMetricOutputV1> {
  const cancellationGroup = `trendFetchMetric${CANCELLATION_GROUP_GUID_SEPARATOR}${id}`;
  ifNotTableBuilderView(() => setMetricDataStatusLoading(id));
  return cancelGroup(cancellationGroup)
    .then(() => sqMetricsApi.getMetric({ id }, { cancellationGroup }))
    .then((result) => {
      const data = result.data;
      if (data) {
        if (data.neutralColor === SeeqNames.Properties.Unset) {
          data.neutralColor = '#ffffff';
        }
        data.thresholds?.forEach(({ priority }) => {
          if (priority?.color === SeeqNames.Properties.Unset) {
            priority.color = '#ffffff';
          }
        });
      }
      ifNotTableBuilderView(() => setMetricDataStatusPresent({ id }));
      return result;
    })
    .catch((e) => catchItemDataFailure(id, cancellationGroup, e)) as AxiosPromise<ThresholdMetricOutputV1>;

  function ifNotTableBuilderView(action: () => void) {
    if (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TABLE) {
      action();
    }
  }
}

export function addMetricChildren(itemId: string, fetchedMetric: ThresholdMetricOutputV1, skipDependencies = false) {
  // Set metric first so definition is available when children are added
  const props = _.omitBy(
    {
      definition: fetchedMetric,
      valueUnitOfMeasure: fetchedMetric.valueUnitOfMeasure,
    },
    _.isUndefined,
  );
  setTrendItemProps(itemId, props, PUSH_IGNORE);

  // Make the item type be that which will be returned when the formula function is executed and set the fragments
  // property to be an object where the key is the parameter name and the value is the formula fragment type that
  // should be used when the signal is fetched (in this case a capsule formula for the current display range).
  const displayItemProps = {} as any;
  const displayItem = _.cloneDeep(fetchedMetric.displayItem) as any;
  if (displayItem.type === API_TYPES.FORMULA_FUNCTION) {
    displayItem.itemType = ITEM_TYPES.SERIES;
    displayItemProps.fragments = {
      capsule: FORMULA_FRAGMENT_TYPE.DISPLAY_RANGE,
    };
  }
  if (displayItem.type === API_TYPES.CALCULATED_SIGNAL) {
    // Prevents a brief switch to linear interpolation in-between when this item is created and when the data comes
    // back (which will have the interpolation method). This is safe to set to Step because the metric always
    // creates an aggregated signal with Step interpolation, except for `totalized`, but that stat has a small flicker
    // even when forced to Linear.
    displayItemProps.interpolationMethod = 'Step';
  }

  addChildItem(
    itemId,
    ITEM_CHILDREN_TYPES.METRIC_DISPLAY,
    displayItem,
    displayItemProps,
    PUSH_IGNORE,
    skipDependencies,
  );

  // Ordering by priority makes things easier to reason about here
  const thresholds = _.chain(fetchedMetric.thresholds)
    .cloneDeep()
    .concat({
      item: displayItem,
      priority: { level: 0, color: fetchedMetric.neutralColor },
    } as ThresholdOutputV1)
    .sortBy('priority.level')
    .value() as ThresholdOutputV1[];
  if (
    fetchedMetric.valueUnitOfMeasure !== 'string' &&
    _.every(thresholds, (threshold) => _.includes(SHADED_AREA_TYPES, getItemType(threshold.item as any)))
  ) {
    // Display thresholds as shaded areas on the trend
    _.forEach(thresholds, (threshold, i) => {
      const baseParams = {
        parentId: itemId,
        childType: ITEM_CHILDREN_TYPES.METRIC_THRESHOLD,
        baseItem: threshold.item,
        props: {
          // Include the priority level so the same signal used for multiple thresholds doesn't cause unexpected
          // results
          id: `${itemId}_${threshold.item!.id}_${threshold.priority!.level}`,
          dashStyle: DASH_STYLES.SOLID,
          color: threshold.priority!.color,
          axisVisibility: false,
          fillOpacity: 0.09,
          valueUnitOfMeasure: threshold?.value?.uom ?? '',
        },
      };
      const maybeUpperItem = _.get(thresholds, [i + 1, 'item']);
      const maybeLowerItem = _.get(thresholds, [i - 1, 'item']);

      if (threshold.priority!.level === 0) {
        if ((maybeLowerItem || maybeUpperItem) && threshold.priority!.color !== 'unset') {
          addShadedArea(
            {
              ...baseParams,
              baseItem: { ...threshold.item, isNeutral: true },
              lower: maybeLowerItem,
              upper: maybeUpperItem,
            },
            PUSH_IGNORE,
            skipDependencies,
          );
        }
      } else if (threshold.priority!.level > 0) {
        addShadedArea(
          {
            ...baseParams,
            lower: threshold.item,
            upper: maybeUpperItem,
          },
          PUSH_IGNORE,
          skipDependencies,
        );
      } else {
        addShadedArea(
          {
            ...baseParams,
            lower: maybeLowerItem,
            upper: threshold.item,
          },
          PUSH_IGNORE,
          skipDependencies,
        );
      }
    });
  } else {
    // Display thresholds as dotted lines on the trend
    _.chain(thresholds)
      .reject((threshold) => threshold.priority!.level === 0)
      .forEach((threshold, i) => {
        const props = {
          // Include the priority level so the same signal used for multiple thresholds doesn't cause unexpected
          // results
          id: `${itemId}_${threshold.item!.id}_${threshold.priority!.level}`,
          dashStyle: DASH_STYLES.DASH,
          color: threshold.priority!.color,
          axisVisibility: false,
        };
        addChildItem(
          itemId,
          ITEM_CHILDREN_TYPES.METRIC_THRESHOLD,
          threshold.item!,
          props,
          PUSH_IGNORE,
          skipDependencies,
        );
      })
      .value();
  }
}

/**
 * Legacy code to support old ancillary boundaries that have been converted to threshold metrics. Looks through all
 * metrics that have the parent as a measured item and which have at least one of the boundaries as a threshold.
 * If so, then adds those thresholds with the old boundary look.
 *
 * @param parent - The parent item, from the sqTrendSeriesStore
 * @param boundaryIds - The ids of items that were used as the boundary thresholds
 */
export function addLegacyBoundaries(parent: any, boundaryIds: string[]) {
  if (!_.isEmpty(boundaryIds)) {
    sqItemsApi
      .getItemUsages({ id: parent.id, scope: [sqWorkbenchStore.stateParams.workbookId] })
      .then(({ data: { items } }) => {
        _.chain(items)
          .filter({ type: SeeqNames.Types.ThresholdMetric })
          .forEach(({ id }) =>
            sqMetricsApi.getMetric({ id }).then(({ data: metric }) => {
              if (
                metric.measuredItem.id === parent.id &&
                _.some(metric.thresholds, (threshold) => _.includes(boundaryIds, threshold.item?.id))
              ) {
                const lower = _.find(metric.thresholds, (threshold) => threshold.priority!.level < 0)?.item;
                const upper = _.find(metric.thresholds, (threshold) => threshold.priority!.level > 0)?.item;

                const showMigrationToast = () => {
                  warnToast({
                    messageKey: 'BOUNDARIES_MIGRATED',
                    messageParams: { name: parent.name },
                    buttonLabelKey: 'BOUNDARIES_VIEW',
                    buttonIcon: 'fc fc-metric',
                    buttonIconStyle: 'dark-gray',
                    buttonVariant: 'outline',
                    buttonAction: () =>
                      fetchItemUsages(parent, 'main', [sqWorkbenchStore.stateParams.workbookId], true).then(() => {
                        tabsetChangeTab('sidebar', 'search');
                      }),
                  });
                };

                if (lower && upper) {
                  showMigrationToast();
                  addShadedArea(
                    {
                      parentId: parent.id,
                      childType: ITEM_CHILDREN_TYPES.ANCILLARY,
                      baseItem: metric,
                      lower,
                      upper,
                      props: {
                        axisVisibility: false,
                        fillOpacity: 0.2,
                        lineWidth: 0,
                      },
                    },
                    PUSH_IGNORE,
                  );
                } else if (lower || upper) {
                  showMigrationToast();
                  addChildItem(
                    parent.id,
                    ITEM_CHILDREN_TYPES.ANCILLARY,
                    (lower || upper) as any,
                    {
                      id: `${parent.id}_${metric.id}`,
                      dashStyle: DASH_STYLES.DASH,
                      axisVisibility: false,
                    },
                    PUSH_IGNORE,
                  );
                }
              }
            }),
          )
          .value();
      })
      .catch(_.noop);
  }
}

export function alignMeasuredItemWithMetric(metric: MetricItem | any) {
  const measuredItem = findItemIn(getTrendStores(), _.get(metric, 'definition.measuredItem.id'));
  if (!measuredItem) {
    return;
  }

  const customizationProps = _.omitBy(
    {
      lane: measuredItem.itemType !== ITEM_TYPES.CONDITION ? metric.lane : undefined,
      axisAlign:
        (metric as { valueUnitOfMeasure?: string }).valueUnitOfMeasure === measuredItem.valueUnitOfMeasure
          ? metric.axisAlign
          : undefined,
    },
    _.isNil,
  );
  setCustomizationProps([
    {
      id: measuredItem.id,
      ...customizationProps,
    },
  ]);
  updateLaneDisplay();

  if (metric.selected) {
    setItemSelected(measuredItem, true);
  }
}
